"use client";

import Script from "next/script";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

type TurnstileApi = {
    render: (container: HTMLElement, options: {
        sitekey: string;
        action: string;
        language: string;
        callback: (token: string) => void;
        "expired-callback": () => void;
        "error-callback": () => void;
        "timeout-callback": () => void;
    }) => string;
    remove: (id: string) => void;
};

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

export default function Turnstile({ onToken, resetKey }: {
    onToken: (token: string) => void;
    resetKey: number;
}) {
    const container = useRef<HTMLDivElement>(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(false);
    const locale = useLocale();
    const t = useTranslations("ContactPage.form");
    const sitekey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

    useEffect(() => {
        const api = window.turnstile;
        if (!ready || !api || !container.current || !sitekey) return;

        setError(false);
        onToken("");
        const invalidate = () => {
            onToken("");
            setError(true);
        };
        let id: string;
        try {
            id = api.render(container.current, {
                sitekey,
                action: "contact",
                language: locale,
                callback: (token) => {
                    setError(false);
                    onToken(token);
                },
                "expired-callback": invalidate,
                "error-callback": invalidate,
                "timeout-callback": invalidate,
            });
        } catch {
            invalidate();
            return;
        }
        return () => {
            try {
                api.remove(id);
            } catch {
                // A failed widget cleanup must not interrupt navigation or unmounting.
            } finally {
                onToken("");
            }
        };
    }, [ready, sitekey, locale, onToken, resetKey]);

    return (
        <div>
            {sitekey && <Script
                src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
                onReady={() => setReady(true)}
                onError={() => { onToken(""); setError(true); }}
            />}
            <div ref={container} />
            {(error || !sitekey) && <p role="alert" className="text-red-600">{t("captchaError")}</p>}
        </div>
    );
}
